import './DraggableProductDimension.scss';
import {ReactNode, useMemo, useState, useRef, useEffect} from "react";
import { Tooltip } from 'antd';
import {SortType} from "@/types/DodRun";
import {ByzzerCheckableChangeEvent, ByzzerCheckbox, ByzzerTipIcon} from "@byzzer/ui-components";
import {Draggable} from "react-beautiful-dnd";
import classnames from "classnames";
import {SortToggle} from "@/components/DodConfigEditor/builders/DodLayoutBuilder/DodDesignYourLayout/SortToggle";
import {RowColConfig} from "@/components/DodConfigEditor/types";
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import {useEventDataWithUserInfo, useTrackEvent} from "@/analytics/AnalyticsContext";
import { intersection } from 'lodash';
import { cdiBdiFactIds, weeksSellingFactIds, isWorkInProgressFact } from '@/components/DodConfigEditor/common/utils';
import Tippy from '@tippyjs/react';

interface DraggableProductDimensionProps {
    className?: string;
    label: ReactNode;
    config: RowColConfig;
    index: number;
    hiddenTip?: ReactNode;
    hideStack?: boolean;  
    onConfigChange?(config: RowColConfig): void;
    mode?: string|null;
}

const baseClassName = 'draggable-product-dimension';
const newLayoutMode = 'layout';

function SubDimensionLabel({ label, disabled }: { label: ReactNode, disabled: boolean }) {
    const [isLabelOverflowing, setIsLabelOverflowing] = useState(false);
    const labelRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        if (labelRef.current) {
            setIsLabelOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
        }
    }, [label]);

    return (
        <div
            className={`${baseClassName}__label ${isLabelOverflowing ? `${baseClassName}__label--overflow` : ''} ${disabled ? `${baseClassName}__label--disabled` : ''}`}
            ref={labelRef}
        >
            <Tooltip placement="topLeft" title={isLabelOverflowing && label} overlayClassName={`${baseClassName}__label--tooltip`}>
                {label}
            </Tooltip>
        </div>
    );
}

export function DraggableProductDimension({
                                              className,
                                              label,
                                              config,
                                              index,
                                              onConfigChange,
                                              hiddenTip,
                                              hideStack = false,
                                              mode,
                                              ...props
                                          }: DraggableProductDimensionProps) {

    const stackable = config.axis === 'row' && !hideStack;
    const { runConfig, setRunConfig } = useDodWizard();
    const trackEvent = useTrackEvent();
    const getEventData = useEventDataWithUserInfo();
    const hasSummedSelections = getProductSummedSelectionStatus(config.dim)
    const isApplyNewLayoutMode = mode === newLayoutMode;
    const disableHide = useMemo(() => {
        const { dim } = config;
        let disable = false;
        let tip = "";

        for (const fact of runConfig.facts) {
            if (
                (dim === 'categories' && (fact.category || fact.categoryYearAgo || fact.categoryTwoYearAgo || fact.categoryThreeYearAgo || fact.categoryFourYearAgo)) ||
                (dim === 'brands' && (fact.brand || fact.brandYearAgo || fact.brandTwoYearAgo || fact.brandThreeYearAgo || fact.brandFourYearAgo)) ||
                (dim === 'upcs' && fact.isUPCrequired) ||
                (dim === 'subcategories' && (fact.subCategory || fact.subCategoryYearAgo || fact.subCategoryTwoYearAgo || fact.subCategoryThreeYearAgo || fact.subCategoryFourYearAgo))
            ) {
                disable = true;
                tip = dim === 'categories'
                ? 'You cannot hide Category when you have Share of Category facts selected.'
                : dim === 'upcs'
                ? 'You cannot hide UPC when you have UPC-level facts selected.'
                : dim === 'brands' 
                ? 'You cannot hide Brand when you have Share of Brand facts selected.'
                : 'You cannot hide Subcategory when you have Share of Subcategory facts selected.'
                break;
            }
        }
        return { disable, tip };
    }, [runConfig, config]);
    const disabledByInProgressFacts = useMemo(() => {
        return isWorkInProgressFact(runConfig.facts);
    }, [runConfig.facts]);

    function getProductSummedSelectionStatus(productSubDimensionType: string) {
        if (runConfig.filters[productSubDimensionType] && Boolean(runConfig.filters[productSubDimensionType].summedSelections.length)) {
            return true
        } else if (Boolean(Object.values(runConfig.filters.characteristics).find((item) => item.displayName === config.dim)?.summedSelections.length)) {
            return true
        } else if (Boolean(Object.values(runConfig.filters.customCharacteristics).find((item) => item.displayName === config.dim)?.summedSelections.length)) {
            return true
        } else if (Boolean(Object.values(runConfig.filters.ppgs).find((item) => item.displayName === config.dim)?.summedSelections.length)) {
            return true
        }else {
            return false
        }
    }

    function checkFactsForDisable(facts, conditions, tipGenerator) {
        for (const fact of facts) {
            if (conditions(fact)) {
                return {
                    disable: true,
                    tip: tipGenerator(),
                };
            }
        }
        return { disable: false, tip: '' };
    }

    const disableStackForProducts = useMemo(() => {
        return checkFactsForDisable(
            runConfig.facts,
            (fact) =>
                fact.productSelections ||
                fact.productSelectionsFourYearAgo ||
                fact.productSelectionsYearAgo ||
                fact.productSelectionsTwoYearAgo ||
                fact.productSelectionsThreeYearAgo,
            () => `You cannot stack ${label} because you selected Share of Product Selections facts. Remove these facts if you wish to stack ${label}.`
        );
    }, [runConfig, label]);

    const disableStack = useMemo(() => {
        const { dim } = config;

        return checkFactsForDisable(
            runConfig.facts,
            (fact) =>
                (dim === 'categories' &&
                    (fact.category ||
                        fact.categoryYearAgo ||
                        fact.categoryTwoYearAgo ||
                        fact.categoryThreeYearAgo ||
                        fact.categoryFourYearAgo)) ||
                (dim === 'brands' &&
                    (fact.brand ||
                        fact.brandYearAgo ||
                        fact.brandTwoYearAgo ||
                        fact.brandThreeYearAgo ||
                        fact.brandFourYearAgo)) ||
                (dim === 'subcategories' &&
                    (fact.subCategory ||
                        fact.subCategoryYearAgo ||
                        fact.subCategoryTwoYearAgo ||
                        fact.subCategoryThreeYearAgo ||
                        fact.subCategoryFourYearAgo)),
            () =>
                dim === 'categories'
                    ? 'You cannot stack Category because you selected Share of Category facts. Remove these facts if you wish to stack Category.'
                    : dim === 'brands'
                    ? 'You cannot stack Brand because you selected Share of Brand facts. Remove these facts if you wish to stack Brand.'
                    : 'You cannot stack Subcategory because you selected Share of Subcategory facts. Remove these facts if you wish to stack Subcategory.'
        );
    }, [runConfig, config]);

    const pageByDisabledTip = useMemo<ReactNode | undefined>(() => {
        if (config.stack) {
            return <>
                <p>A dimension cannot be Paged if it is Stacked</p>
                <p>Please uncheck Stack first.</p>
            </>
        }

        if (config.hide) {
            return <>
                <p>A hidden dimension cannot be Paged</p>
                <p>Please uncheck Hide first.</p>
            </>
        }
    }, [config.stack, config.hide]);

    const stackDisabledTip = useMemo<ReactNode | undefined>(() => {
        if (config.pageBy) {
            return <>
                <p>A dimension cannot be Stacked if it is Paged</p>
                <p>Please uncheck "Page By" first.</p>
            </>
        }

        if (config.hide) {
            return <>
                <p>A hidden dimension cannot be Stacked.</p>
                <p>Please uncheck Hide first.</p>
            </>
        }

        if (hasSummedSelections) {
            return <>
                <p>Summed products and stacked layout are currently not compatible.</p>
                <p>Please choose a different layout option.</p>
            </>
        }

    }, [config.pageBy, config.hide])

    function handleSortChange(sortType: SortType): void {
        onConfigChange?.({
            ...config,
            sortType
        })

        trackEvent({
            type: 'click',
            name: 'dod_layout_sort_click',
            data: getEventData({ dodWizardStep: 'layout', filter: config.dim, order: sortType }),
        });
    }

    function handleFlagChange(e: ByzzerCheckableChangeEvent<string>): void {
        onConfigChange?.({
            ...config,
            [e.name!]: e.checked
        })

        trackEvent({
            type: 'click',
            name: e.checked ? `dod_layout_product_${e.name}_checked` : `dod_layout_product_${e.name}_unchecked`,
            data: getEventData({ dodWizardStep: 'layout', filter: 'product', type: config.dim}),
        });
    }

    function handleHideChange(e: ByzzerCheckableChangeEvent<string>): void {
        if(config.dim === 'categories' && e.checked) {
            // when category is hidden, category totals need to reset to false and disabled
            setRunConfig({...runConfig, layout: {
                ...runConfig.layout,
                includeCategoryTotals: false
            }})
        }
        onConfigChange?.({
            ...config,
            hide: e.checked,
            pageBy: config.pageBy && !e.checked,
            stack: config.stack && !e.checked
        })

        trackEvent({
            type: 'click',
            name: e.checked ? 'dod_layout_product_hide_checked' : 'dod_layout_product_hide_unchecked',
            data: getEventData({ dodWizardStep: 'layout', filter: 'product', type: config.dim }),
        });
    }

    //BYZ-12113 - Hide and stack option should be disabled always if any of the market in run is partially-approved
    const hasPartiallyApprovedMarkets = useMemo(() => {
        if (
            config.dim === 'categories' &&
            runConfig.filters.markets.values.some((item) => item.marketHasPartialApproval === true)
        ) {
            onConfigChange?.({
                ...config,
                hide: false,
                stack: false,
            });
            return true;
        } else {
            return false;
        }
    }, [runConfig.filters.markets.values, config.dim]);

    // Tooltip message for the disabled state
    const getDisabledTooltip = (disableState, hasPartiallyApprovedMarkets) => {
        if (disableState.disable) return disableState.tip;

        if (hasPartiallyApprovedMarkets) {
            return "You are not able to hide Category because some of your selected markets require prior approval. Please reach out to your NIQ rep to get approval.";
        }

        return null;
    };
    // Tooltip for the disabled button
    const disabledTip = getDisabledTooltip(disableHide, hasPartiallyApprovedMarkets); 
    
    const isWorkInProgressFactsSelected = () => {
        return Boolean(intersection(
            runConfig.facts.map((fact) => fact.id),
            [...weeksSellingFactIds, ...cdiBdiFactIds]
        ).length)
    }

    const applyNewLayoutDisabledTip = useMemo<ReactNode | undefined>(() => {
        if (mode === newLayoutMode) {
            return <>
                <p>This selection type is not currently compatible with Apply New Layout.
                Please use the modify option if you wish to change this aspect of your layout.</p>                
            </>
        }        
    }, [mode]);

    return (
        <Draggable isDragDisabled={isApplyNewLayoutMode ? true : false} draggableId={config.dim} index={index}>
            {(provided, snapshot) => (
                <div
                    className={classnames(baseClassName, className)}
                    {...props}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                >
                    {isApplyNewLayoutMode ? (
                        <Tippy content={isApplyNewLayoutMode && applyNewLayoutDisabledTip}>
                            <i className={`${baseClassName}__dnd-handle--disabled`} />
                        </Tippy>
                    ) : (
                        <i className={`${baseClassName}__dnd-handle`} />
                    )}

                    <SortToggle
                        value={config.sortType}
                        onChange={handleSortChange}
                        manualTip={
                            <>
                                <p>Sorted Using Default (Ascending)</p>
                                <p>Click to toggle.</p>
                            </>
                        }
                        sortable={!isApplyNewLayoutMode}
                    />
                    <SubDimensionLabel label={label} disabled={isApplyNewLayoutMode} />
                    <div className={`${baseClassName}__actions`}>
                        <ByzzerCheckbox
                            checked={config.hide}
                            name={'hide'}
                            onChange={handleHideChange}
                            disabled={
                                hasPartiallyApprovedMarkets ||
                                disableHide.disable ||
                                disabledByInProgressFacts.disabled ||
                                isApplyNewLayoutMode
                            }
                            disabledTip={
                                disabledByInProgressFacts.disabled
                                    ? disabledByInProgressFacts.hoverText
                                    : isApplyNewLayoutMode
                                    ? applyNewLayoutDisabledTip
                                    : disabledTip
                            }
                            label={
                                <div className={`${baseClassName}__hide-label`}>
                                    Hide{' '}
                                    <ByzzerTipIcon
                                        tipDelay={[750, 0]}
                                        tip={<div className={`${baseClassName}__hide-tip-content`}>{hiddenTip}</div>}
                                        className={classnames(`${baseClassName}__hide-tip`, {
                                            [`${baseClassName}__hide-tip--exists`]: Boolean(hiddenTip),
                                        })}
                                    />
                                </div>
                            }
                        />
                        {stackable && (
                            <ByzzerCheckbox
                                checked={config.stack}
                                name={'stack'}
                                onChange={handleFlagChange}
                                disabledTip={
                                    disableStack.disable
                                        ? disableStack.tip
                                        : disableStackForProducts.disable
                                        ? disableStackForProducts.tip
                                        : disabledByInProgressFacts.disabled
                                        ? disabledByInProgressFacts.hoverText
                                        : isApplyNewLayoutMode
                                        ? applyNewLayoutDisabledTip
                                        : stackDisabledTip
                                }
                                disabled={
                                    config.hide ||
                                    config.pageBy ||
                                    hasPartiallyApprovedMarkets ||
                                    hasSummedSelections ||
                                    disabledByInProgressFacts.disabled ||
                                    isApplyNewLayoutMode || 
                                    disableStack.disable || 
                                    disableStackForProducts.disable
                                }
                                label={'Stack'}
                            />
                        )}
                        <ByzzerCheckbox
                            checked={config.pageBy}
                            name={'pageBy'}
                            onChange={handleFlagChange}
                            disabledTip={pageByDisabledTip}
                            disabled={config.hide || config.stack}
                            label={'Page By'}
                        />
                    </div>
                </div>
            )}
        </Draggable>
    );
}
